import React, { useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMutation } from "../../hooks/useExclusiveMutation";
import toast from "react-hot-toast";

import { budgetsApi, eventsApi, schedulesApi } from "../../api";
import { MobileSelect } from "../ui/MobileSelect";
import { rsvpApi } from "../../api/rsvp";
import { APP_WORKSPACE } from "../../constants/appWorkspace";
import { absoluteUrl } from "../../config/site";
import { useAuth } from "../../context/AuthContext";
import { useDeviceTier } from "../../hooks/useDeviceTier";
import { useScrollToTopOnNavigate } from "../../hooks/useScrollToTopOnNavigate";
import { queryKeys } from "../../queryKeys";
import { appNav } from "../../routes/appNavigation";
import { writeLastEventId, type EventWorkspaceBackState } from "../../routes/workspaceNavigation";
import { EVENT_STATUS_OPTIONS } from "../../utils/eventForm";
import { EventInviteQrCard } from "../events/EventInviteQrCard";
import { EventSetupChecklist } from "./EventSetupChecklist";
import { ShowHealthRail } from "./ShowHealthRail";
import "./EventWorkspaceLayout.css";

const TABS = [
  { id: "overview", label: "Overview", path: (id: string) => appNav.eventOverview(id) },
  { id: "deals", label: "Deals", path: (id: string) => appNav.eventDeals(id) },
  { id: "budget", label: "Budget & Costs", path: (id: string) => appNav.eventBudget(id) },
  { id: "pnl", label: "P&L", path: (id: string) => appNav.eventPnl(id) },
  { id: "run-of-show", label: "Run of Show", path: (id: string) => appNav.eventRunOfShow(id) },
  { id: "tickets", label: "Tickets", path: (id: string) => appNav.eventTickets(id) },
  { id: "live", label: "Live Ops", path: (id: string) => appNav.eventLive(id) },
  { id: "rsvp", label: "RSVP", path: (id: string) => appNav.eventRsvp(id) },
  { id: "invites", label: "Invites", path: (id: string) => appNav.eventInvites(id) },
  { id: "invoices", label: "Invoices", path: (id: string) => appNav.eventInvoices(id) },
  { id: "contacts", label: "Contacts", path: (id: string) => appNav.eventContacts(id) },
  { id: "notes", label: "Notes", path: (id: string) => appNav.eventNotes(id) },
  { id: "files", label: "Files", path: (id: string) => appNav.eventFiles(id) },
] as const;

export const EventWorkspaceLayout: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const { pathname, state } = useLocation();
  const backState = (state ?? null) as EventWorkspaceBackState | null;
  const backTo = backState?.backTo || appNav.events();
  const backLabel = backState?.backLabel || "Events";
  const isLiveTab = pathname.endsWith("/live");
  const isRsvpTab = pathname.endsWith("/rsvp");
  const isTicketsTab = pathname.endsWith("/tickets");
  const isInvoicesTab = Boolean(eventId) && pathname.includes(`/events/${eventId}/invoices`);
  const isOverviewTab = Boolean(eventId) && pathname.includes(`/events/${eventId}/overview`);
  const isPnlTab = Boolean(eventId) && pathname.includes(`/events/${eventId}/pnl`);
  const navigate = useNavigate();
  const isMobile = useDeviceTier() === "mobile";
  /* Which section the picker should show. Matched longest-id-first so
     "run-of-show" isn't shadowed by a shorter id, and defaulted to overview
     for the bare /events/:id path. */
  const activeTabId =
    [...TABS]
      .sort((a, b) => b.id.length - a.id.length)
      .find((t) => eventId && pathname.includes(`/events/${eventId}/${t.id}`))?.id ?? "overview";
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const menuRef = useRef<HTMLDivElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  useScrollToTopOnNavigate();

  const dashboardQuery = useQuery({
    queryKey: eventId && token ? queryKeys.events.dashboard(APP_WORKSPACE, eventId) : queryKeys.events.all(APP_WORKSPACE),
    queryFn: () => eventsApi.dashboard(eventId!, token!),
    enabled: Boolean(token && eventId),
    staleTime: 5_000,
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });

  const eventDetailQuery = useQuery({
    queryKey: eventId && token ? queryKeys.events.detail(APP_WORKSPACE, eventId) : queryKeys.events.all(APP_WORKSPACE),
    queryFn: () => eventsApi.detail(eventId!, token!),
    enabled: Boolean(token && eventId),
    staleTime: 10_000,
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });

  const budgetsQuery = useQuery({
    queryKey: eventId && token ? queryKeys.budgets(APP_WORKSPACE, eventId) : queryKeys.events.all(APP_WORKSPACE),
    queryFn: () => budgetsApi.list(eventId!, token!),
    enabled: Boolean(token && eventId),
  });

  const schedulesQuery = useQuery({
    queryKey: eventId && token ? queryKeys.schedules(APP_WORKSPACE, eventId) : queryKeys.events.all(APP_WORKSPACE),
    queryFn: () => schedulesApi.list(eventId!, token!),
    enabled: Boolean(token && eventId),
  });

  const rsvpSettingsQuery = useQuery({
    queryKey: eventId ? queryKeys.rsvpSettings(APP_WORKSPACE, eventId) : ["rsvp", "idle"],
    queryFn: () => rsvpApi.getSettings(eventId!, token!),
    enabled: Boolean(token && eventId && isTicketsTab),
  });

  const walkInUrl =
    rsvpSettingsQuery.data?.walk_in_token
      ? absoluteUrl(appNav.publicWalkIn(rsvpSettingsQuery.data.walk_in_token))
      : "";

  const statusMutation = useMutation({
    mutationFn: async (status: string) => {
      if (!eventId || !token) throw new Error("Missing event context");
      return eventsApi.update(eventId, { status }, token);
    },
    onSuccess: async () => {
      toast.success("Status updated");
      if (eventId) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.events.dashboard(APP_WORKSPACE, eventId) });
        await queryClient.invalidateQueries({ queryKey: queryKeys.events.list(APP_WORKSPACE) });
      }
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to update status");
    },
  });

  React.useEffect(() => {
    if (eventId) writeLastEventId(APP_WORKSPACE, eventId);
  }, [eventId]);

  React.useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  React.useEffect(() => {
    bodyRef.current?.scrollTo(0, 0);
  }, [pathname]);

  const event = dashboardQuery.data?.event;

  const tabClass = ({ isActive }: { isActive: boolean }) =>
    `event-workspace__tab${isActive ? " event-workspace__tab--active" : ""}`;

  const headerTitle = event?.name ?? (dashboardQuery.isPending ? "Loading event…" : "Event workspace");

  const exportPnlCsv = () => {
    if (!event) return;
    const rows = [
      ["Event", event.name],
      ["City", event.city],
      ["Date", event.event_date],
      ["Status", event.status],
    ];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${event.name.replace(/[^\w-]+/g, "_")}_summary.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    setMenuOpen(false);
    toast.success("Export started");
  };

  return (
    <div
      className={`event-workspace${isLiveTab ? " event-workspace--live" : ""}${isRsvpTab ? " event-workspace--rsvp" : ""}${isTicketsTab ? " event-workspace--tickets" : ""}${isInvoicesTab ? " event-workspace--invoices" : ""}${isOverviewTab ? " event-workspace--overview" : ""}${isPnlTab ? " event-workspace--pnl" : ""}`}
    >
      <div className="event-workspace__chrome">
        <header className="event-workspace__header">
          <Link to={backTo} className="app-shell-back-link event-workspace__back">
            ← {backLabel}
          </Link>
          <div className="event-workspace__header-main">
            <div className="event-workspace__title-line">
              <h1 className="app-shell-page-title event-workspace__title">{headerTitle}</h1>
              {eventDetailQuery.data?.ticketing_linked ? (
                <span className="event-workspace__linked-badge" title="Paid zoniq.in sales sync into this event automatically">
                  Linked to zoniq.in
                  {eventDetailQuery.data.ticketing_event_id
                    ? ` #${eventDetailQuery.data.ticketing_event_id}`
                    : ""}
                </span>
              ) : null}
              {event ? (
                <p className="app-shell-lede event-workspace__lede">
                  {event.city} · {event.event_date}
                  {event.venue_capacity ? ` · capacity ${event.venue_capacity.toLocaleString("en-IN")}` : ""}
                  {event.artist ? ` · ${event.artist}` : ""}
                </p>
              ) : null}
            </div>
            <div className="event-workspace__header-actions">
              {event ? (
                <label className="event-workspace__status-select">
                  <span className="visually-hidden">Event status</span>
                  <MobileSelect
                    label="Event status"
                    className="eventos-select event-workspace__status-input"
                    value={event.status}
                    disabled={statusMutation.isPending}
                    onChange={(v) => statusMutation.mutate(v)}
                    options={[
                      ...EVENT_STATUS_OPTIONS.map((s) => ({ value: s, label: s.replace(/_/g, " ") })),
                      // a status the backend set that is not in the canonical list stays selectable
                      ...(EVENT_STATUS_OPTIONS.includes(event.status as (typeof EVENT_STATUS_OPTIONS)[number])
                        ? []
                        : [{ value: event.status, label: event.status.replace(/_/g, " ") }]),
                    ]}
                  />
                </label>
              ) : null}
              {eventId ? (
                <div className="event-workspace__menu" ref={menuRef}>
                  <button
                    type="button"
                    className="eventos-nav-btn event-workspace__kebab"
                    aria-label="Event actions"
                    aria-expanded={menuOpen}
                    onClick={() => setMenuOpen((v) => !v)}
                  >
                    ⋯
                  </button>
                  {menuOpen ? (
                    <div className="event-workspace__menu-dropdown" role="menu">
                      <Link to={appNav.editEvent(eventId)} className="event-workspace__menu-item" role="menuitem" onClick={() => setMenuOpen(false)}>
                        Edit event
                      </Link>
                      <Link to={appNav.eventLive(eventId)} className="event-workspace__menu-item" role="menuitem" onClick={() => setMenuOpen(false)}>
                        Open Live Ops
                      </Link>
                      <button type="button" className="event-workspace__menu-item" role="menuitem" onClick={exportPnlCsv}>
                        Export summary CSV
                      </button>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </header>

        {isMobile && eventId ? (
          /* Twelve tabs in a horizontally scrolling strip means the sections
             past the fourth are invisible and reached by guesswork. A native
             picker shows every section at once, names where you are, and
             costs one tap. */
          <div className="event-workspace__section-picker">
            <MobileSelect
              id="event-section"
              label="Event section"
              value={activeTabId}
              options={TABS.map((tab) => ({ value: tab.id, label: tab.label }))}
              onChange={(next) => {
                const tab = TABS.find((t) => t.id === next);
                if (tab) navigate(tab.path(eventId), { state: backState ?? undefined });
              }}
            />
          </div>
        ) : (
          <nav className="event-workspace__tabs" aria-label="Event workspace">
            {eventId
              ? TABS.map((tab) => (
                  <NavLink
                    key={tab.id}
                    to={tab.path(eventId)}
                    state={backState ?? undefined}
                    className={tabClass}
                    end={tab.id === "overview"}
                  >
                    {tab.label}
                  </NavLink>
                ))
              : null}
          </nav>
        )}
      </div>

      <div className="event-workspace__body" ref={bodyRef}>
        {eventId && dashboardQuery.data && isOverviewTab ? (
          <div className="event-workspace__overview-top">
            <EventSetupChecklist
              eventId={eventId}
              data={dashboardQuery.data}
              budgetCount={(budgetsQuery.data ?? []).length}
              scheduleCount={(schedulesQuery.data ?? []).length}
            />
            <aside className="event-workspace__rail event-workspace__rail--overview" aria-label="Show health">
              <ShowHealthRail
                data={dashboardQuery.data}
                compact
                className="show-health-rail--inline"
                sections={["health", "budget", "sellthrough"]}
              />
            </aside>
          </div>
        ) : null}
        <div className="event-workspace__main">
          <Outlet />
        </div>
        {eventId && dashboardQuery.data && isTicketsTab ? (
          <aside className="event-workspace__rail event-workspace__rail--compact" aria-label="Show health">
            <ShowHealthRail data={dashboardQuery.data} compact sections={["health"]} />
            <EventInviteQrCard
              url={walkInUrl}
              qrId={`tickets-event-qr-${eventId}`}
              eventName={dashboardQuery.data.event.name}
              eventDate={dashboardQuery.data.event.event_date}
              venue={dashboardQuery.data.event.city}
            />
            <ShowHealthRail data={dashboardQuery.data} compact sections={["sellthrough"]} />
          </aside>
        ) : null}
        {eventId && dashboardQuery.data && isPnlTab ? (
          <aside className="event-workspace__rail event-workspace__rail--compact" aria-label="Show health">
            <ShowHealthRail
              data={dashboardQuery.data}
              compact
              sections={["health", "budget", "sellthrough"]}
            />
          </aside>
        ) : null}
      </div>
    </div>
  );
};
